/**
 * Render IRGraph as a draw.io UML Activity Diagram with swimlanes.
 *
 * This is the primary output format — matching the Python renderer's output
 * with activity-diagram-specific shapes (start/end circles, fork bars, etc.).
 */

import type { LayoutResult, IRNode, ColorPalette } from '../types';
import { NodeShape } from '../types';
import { SHAPE_REGISTRY, EDGE_STYLE, EDGE_STYLE_DASHED, CONTAINER_STYLE, LANE_STYLE } from '../registry';
import { getShapeColors, applyColors } from '../color';
import { IdGen, wrapMxfile, vertexCell, edgeCell, labelCell } from './xml-builder';

export function renderDrawioActivity(
  layout: LayoutResult,
  palette: ColorPalette,
): string {
  const gen = new IdGen();
  const graph = layout.graph;
  const cells: string[] = [];

  // Container (swimlane parent)
  const containerId = gen.next();
  cells.push(
    vertexCell(
      containerId,
      '',
      CONTAINER_STYLE,
      '1',
      layout.containerX,
      layout.containerY,
      layout.containerWidth,
      layout.containerHeight,
    ),
  );

  const nodeCellMap = new Map<string, string>();
  const laneCellMap = new Map<string, string>();

  // Render lanes and nodes
  for (const lane of graph.lanes) {
    const laneId = gen.next();
    laneCellMap.set(lane.id, laneId);
    cells.push(
      vertexCell(
        laneId,
        lane.title,
        LANE_STYLE,
        containerId,
        lane.x,
        lane.y,
        lane.width,
        lane.height,
      ),
    );

    // Nodes in this lane
    for (const nid of lane.nodeIds) {
      const node = graph.nodes.get(nid);
      if (!node || node.x === undefined || node.y === undefined) continue;

      const cellId = gen.next();
      nodeCellMap.set(nid, cellId);

      let style = getActivityStyle(node);
      const colors = getShapeColors(node.shape, palette);
      style = applyColors(style, colors);

      const value = isBlankShape(node.shape) ? '' : node.label;

      cells.push(
        vertexCell(cellId, value, style, laneId, node.x, node.y, node.width, node.height),
      );
    }
  }

  // Render edges
  for (const edge of graph.edges) {
    const srcCell = nodeCellMap.get(edge.source);
    const tgtCell = nodeCellMap.get(edge.target);
    if (!srcCell || !tgtCell) continue;

    // Parent: lane if intra-lane, container if cross-lane
    let parent: string;
    if (edge.crossLane) {
      parent = containerId;
    } else {
      const srcNode = graph.nodes.get(edge.source);
      parent =
        srcNode?.laneId && laneCellMap.has(srcNode.laneId)
          ? laneCellMap.get(srcNode.laneId)!
          : containerId;
    }

    let style = edge.dashed ? EDGE_STYLE_DASHED : EDGE_STYLE;

    // Apply pre-computed decision exit points
    const exitPt = edge.meta?.exitPoint as { x: number; y: number } | undefined;
    if (exitPt) {
      style += `exitX=${exitPt.x};exitY=${exitPt.y};exitDx=0;exitDy=0;`;
    }

    // Apply entry points (e.g. back-edge routing)
    const entryPt = edge.meta?.entryPoint as { x: number; y: number } | undefined;
    if (entryPt) {
      style += `entryX=${entryPt.x};entryY=${entryPt.y};entryDx=0;entryDy=0;`;
    }

    const routePoints = edge.meta?.routePoints as Array<{ x: number; y: number }> | undefined;
    const edgeId = gen.next();
    cells.push(edgeCell(edgeId, '', style, parent, srcCell, tgtCell, routePoints));

    // Edge label
    if (edge.label) {
      const lblId = gen.next();
      cells.push(labelCell(lblId, edge.label, edgeId));
    }
  }

  return wrapMxfile(cells);
}

function getActivityStyle(node: IRNode): string {
  const entry = SHAPE_REGISTRY[node.shape];
  return entry.activityStyle ?? entry.flowchartStyle ?? 'rounded=1;whiteSpace=wrap;html=1;';
}

function isBlankShape(shape: NodeShape): boolean {
  return [
    NodeShape.StartCircle,
    NodeShape.EndCircle,
    NodeShape.ForkBar,
    NodeShape.JoinBar,
    NodeShape.MergeDiamond,
  ].includes(shape);
}

